import { BreadcrumbItem, StructuredData } from "../components/SEO/types";

/**
 * SEO Utility Functions
 */

/**
 * Generate breadcrumb structured data from breadcrumb items
 */
export const generateBreadcrumbSchema = (
  breadcrumbs: BreadcrumbItem[]
): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Sanitize and truncate text for meta descriptions
 */
export const sanitizeDescription = (
  text: string,
  maxLength: number = 160
): string => {
  const sanitized = text.replace(/\s+/g, " ").trim();
  if (sanitized.length <= maxLength) return sanitized;
  return sanitized.substring(0, maxLength - 3) + "...";
};

/**
 * Generate canonical URL
 */
export const getCanonicalUrl = (
  path: string,
  baseUrl: string = process.env.REACT_APP_SITE_URL || "https://jobshunt.org"
): string => {
  // Remove trailing slash from baseUrl and leading slash from path
  const cleanBase = baseUrl.replace(/\/$/, "");
  const cleanPath = path.replace(/^\//, "");
  return cleanPath ? `${cleanBase}/${cleanPath}` : cleanBase;
};

/**
 * Format title with template
 */
export const formatTitle = (
  title: string | undefined,
  template: string,
  defaultTitle: string
): string => {
  if (!title) return defaultTitle;
  return template.replace("%s", title);
};

/**
 * Generate FAQ schema from FAQ items
 */
export const generateFAQSchema = (
  faqs: Array<{ question: string; answer: string }>
): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate Organization schema with custom data
 */
export const generateOrganizationSchema = (data: {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  contactUrl?: string;
}): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.name || "就活ダッシュボード",
    url: data.url || "https://jobshunt.org",
    logo: data.logo || "https://jobshunt.org/logo192.png",
    description:
      data.description ||
      "就活ダッシュボードは新卒就職活動を効率化する無料ツールです。",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      url: data.contactUrl || "https://jobshunt.org/contact",
    },
  };
};

/**
 * Convert keywords array to comma-separated string
 */
export const formatKeywords = (keywords: string[]): string => {
  return keywords.join(",");
};
